/**
 * InventoryManagerView.tsx
 *
 * Sovereign Mesh Warehouse & Inventory Management Workspace.
 * - Real-time stock ledgers, FEFO batch inspection, dual-legged location transfers.
 * - Multi-batch expiration governance & health matrix.
 * - Quantity-aware partial quarantine holds, releases, and write-offs.
 * - Lot recall reverse-traceability index (findAffectedOrders).
 * - Content-addressed media preview and 3-state allergen profile inspection.
 * - Staged CSV/JSON bulk import/export.
 */

import React, { useState, useEffect } from 'react';
import { globalInventoryMovementLedger } from '../../plugins/commerce/inventory/inventoryMovementLedger.js';
import { StagedImportService } from '../../plugins/commerce/inventory/stagedImportService.js';
import { BatchHealthService, type AffectedOrderRecord } from '../../plugins/commerce/inventory/batchHealthService.js';
import { globalMediaAssetService } from '../../plugins/commerce/media/mediaAssetService.js';
import { globalCatalogService } from '../../plugins/commerce/catalog-service.js';
import { SECURITY_TACTICAL_CATALOG } from '../../plugins/commerce/security-tactical-catalog.js';
import { CreateProductModal } from './CreateProductModal.js';
import { EditListingModal } from './EditListingModal.js';
import type {
  StockProjection,
  WarehouseLocation,
  ImportReport,
  QuarantineReasonCode,
  BatchStock,
} from '../../plugins/commerce/inventory/inventoryContracts.js';
import type { AuthorizationContext } from '../../kernel/authorization/capabilities.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface InventoryManagerViewProps {
  readonly authContext: AuthorizationContext;
}

export const InventoryManagerView: React.FC<InventoryManagerViewProps> = ({ authContext }) => {
  const { t } = useTranslation();
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [projections, setProjections] = useState<readonly StockProjection[]>([]);
  const [locations, setLocations] = useState<readonly WarehouseLocation[]>([]);
  const [expandedSku, setExpandedSku] = useState<string | null>(null);

  // Modals
  const [isCreateProductOpen, setIsCreateProductOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [isReceiveOpen, setIsReceiveOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [quarantineTarget, setQuarantineTarget] = useState<{ batch: BatchStock; mode: 'hold' | 'release' } | null>(null);
  const [recallTargetLot, setRecallTargetLot] = useState<string | null>(null);
  const [affectedOrders, setAffectedOrders] = useState<readonly AffectedOrderRecord[]>([]);

  // Quarantine Form
  const [quarantineQuantity, setQuarantineQuantity] = useState(1);
  const [quarantineReasonCode, setQuarantineReasonCode] = useState<QuarantineReasonCode>('damaged_packaging');
  const [quarantineNotes, setQuarantineNotes] = useState('');

  // Receive Form
  const [receiveForm, setReceiveForm] = useState({
    sku: 'SKU-UBQ-AIPRO',
    locationId: 'loc-main',
    lotNumber: 'LOT-SEC-2026-REC01',
    expiryDate: '2027-12-31',
    quantity: 25,
  });

  // Transfer Form
  const [transferForm, setTransferForm] = useState({
    sku: 'SKU-UBQ-AIPRO',
    sourceLocationId: 'loc-main',
    targetLocationId: 'loc-counter',
    lotNumber: 'LOT-SEC-2026-A1',
    quantity: 5,
  });

  // Adjust Form
  const [adjustForm, setAdjustForm] = useState({
    sku: 'SKU-RMA-LVL4-1165',
    locationId: 'loc-main',
    lotNumber: 'LOT-SEC-2026-A8',
    quantityDelta: -1,
    type: 'writeoff' as 'adjustment' | 'writeoff',
    reason: 'Ballistic stress testing write-off',
  });

  // Import State
  const [importText, setImportText] = useState(
    'sku,productName,locationId,lotNumber,expiryDate,quantity\nSKU-UBQ-AIPRO,Ubiquiti Camera AI Professional,loc-main,LOT-IMP-01,2027-11-30,50\nSKU-RMA-LVL4-1165,RMA Level IV Armor Plate,loc-counter,LOT-IMP-02,2028-10-15,20'
  );
  const [importFormat, setImportFormat] = useState<'csv' | 'json'>('csv');
  const [importReport, setImportReport] = useState<ImportReport | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const importService = new StagedImportService(globalInventoryMovementLedger);
  const healthService = new BatchHealthService(globalInventoryMovementLedger);
  const isPreview = authContext.sessionMode.type === 'role-preview';

  const refreshData = () => {
    setLocations(globalInventoryMovementLedger.getLocations());
    setProjections(globalInventoryMovementLedger.getAllStockProjections(selectedLocation));
  };

  useEffect(() => {
    refreshData();
  }, [selectedLocation]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleReceiveSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      globalInventoryMovementLedger.receiveStock(authContext, {
        sku: receiveForm.sku,
        locationId: receiveForm.locationId,
        lotNumber: receiveForm.lotNumber,
        expiryDate: receiveForm.expiryDate,
        quantity: Number(receiveForm.quantity),
      });
      showToast('success', `Successfully received ${receiveForm.quantity} units of ${receiveForm.sku}`);
      setIsReceiveOpen(false);
      refreshData();
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  const handleTransferSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      globalInventoryMovementLedger.transferStock(authContext, {
        transferId: `tr_${Date.now()}`,
        sku: transferForm.sku,
        sourceLocationId: transferForm.sourceLocationId,
        targetLocationId: transferForm.targetLocationId,
        lotNumber: transferForm.lotNumber,
        quantity: Number(transferForm.quantity),
      });
      showToast('success', `Transferred ${transferForm.quantity} units of ${transferForm.sku}`);
      setIsTransferOpen(false);
      refreshData();
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  const handleAdjustSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      globalInventoryMovementLedger.adjustStock(authContext, {
        sku: adjustForm.sku,
        locationId: adjustForm.locationId,
        lotNumber: adjustForm.lotNumber,
        quantityDelta: Number(adjustForm.quantityDelta),
        type: adjustForm.type,
        reason: adjustForm.reason,
      });
      showToast('success', `Stock adjusted for ${adjustForm.sku}`);
      setIsAdjustOpen(false);
      refreshData();
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  const handleQuarantineSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!quarantineTarget) return;

    try {
      if (quarantineTarget.mode === 'hold') {
        healthService.quarantineBatch(authContext, {
          sku: quarantineTarget.batch.sku,
          locationId: quarantineTarget.batch.locationId,
          lotNumber: quarantineTarget.batch.lotNumber,
          quantity: Number(quarantineQuantity),
          reasonCode: quarantineReasonCode,
          reasonNotes: quarantineNotes || 'Placed in quality hold',
        });
        showToast('success', `Placed ${quarantineQuantity} units of Lot ${quarantineTarget.batch.lotNumber} into quarantine.`);
      } else {
        healthService.releaseQuarantine(authContext, {
          sku: quarantineTarget.batch.sku,
          locationId: quarantineTarget.batch.locationId,
          lotNumber: quarantineTarget.batch.lotNumber,
          quantity: Number(quarantineQuantity),
          reasonCode: quarantineReasonCode,
          reasonNotes: quarantineNotes || 'Released from quality hold',
        });
        showToast('success', `Released ${quarantineQuantity} units of Lot ${quarantineTarget.batch.lotNumber} back to available stock.`);
      }
      setQuarantineTarget(null);
      refreshData();
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  const handleOpenRecallTrace = (lotNumber: string) => {
    setRecallTargetLot(lotNumber);
    const affected = healthService.findAffectedOrders(lotNumber);
    setAffectedOrders(affected);
  };

  const handleGenerateIncident = () => {
    if (!recallTargetLot) return;
    healthService.findAffectedOrders(recallTargetLot, true);
    showToast('success', `🚨 Critical Incident created in Kanban for Lot ${recallTargetLot}.`);
    setRecallTargetLot(null);
  };

  const handleValidateImport = () => {
    const report = importService.validateAndPreviewImport(importText, importFormat);
    setImportReport(report);
  };

  const handleCommitImport = () => {
    if (!importReport) return;
    try {
      const result = importService.commitImport(authContext, importReport);
      showToast('success', `Successfully imported ${result.committedCount} stock movements.`);
      setIsImportOpen(false);
      setImportReport(null);
      refreshData();
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  const handleExportCsv = () => {
    const csv = importService.generateCsvExport(projections);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-export-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleExportJson = () => {
    const json = importService.generateJsonExport(projections);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventory-export-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
  };

  const totalOnHand = projections.reduce((sum, p) => sum + p.onHand, 0);
  const totalReserved = projections.reduce((sum, p) => sum + p.reserved, 0);
  const totalQuarantined = projections.reduce((sum, p) => sum + (p.quarantined || 0), 0);
  const totalAvailable = projections.reduce((sum, p) => sum + p.availableToSell, 0);

  return (
    <div
      style={{
        flex: '1 1 0%',
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--theme-bg-main, #0b0f19)',
        color: 'var(--theme-text-primary, #f8fafc)',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '20px',
        gap: '20px',
      }}
    >
      {/* Toast Notification */}
      {notification && (
        <div
          style={{
            position: 'fixed',
            top: '60px',
            right: '20px',
            zIndex: 100,
            padding: '10px 18px',
            borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#065f46' : '#991b1b',
            color: '#fff',
            fontWeight: 600,
            fontSize: '13px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          }}
        >
          {notification.message}
        </div>
      )}

      {/* Read-Only Preview Warning */}
      {isPreview && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            backgroundColor: 'rgba(245, 158, 11, 0.15)',
            border: '1px solid #f59e0b',
            color: '#fcd34d',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>👁️</span>
          <span>{t('inventory.previewBanner') || 'Role Preview Mode (Read-Only): Mutating inventory actions are simulated.'}</span>
        </div>
      )}

      {/* Header & Global Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
            📦 {t('inventory.title') || 'Warehouse & Inventory Management'}
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--theme-text-secondary, #94a3b8)', fontSize: '13px' }}>
            {t('inventory.subtitle') || 'Multi-batch movement ledger, FEFO allocation, and partial quarantine governance'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setIsCreateProductOpen(true)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-status-success, #10b981)',
              color: '#000',
              fontWeight: 800,
              fontSize: '12px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
            }}
          >
            ➕ {t('inventory.actions.addProduct') || 'Add Product / Item'}
          </button>
          <button
            onClick={() => setIsReceiveOpen(true)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-accent, #38bdf8)',
              color: '#000',
              fontWeight: 700,
              fontSize: '12px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            📥 {t('inventory.actions.receiveStock') || 'Receive Stock'}
          </button>
          <button
            onClick={() => setIsTransferOpen(true)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              color: 'var(--theme-text-primary, #f8fafc)',
              fontWeight: 600,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            🔄 {t('inventory.actions.transferStock') || 'Transfer'}
          </button>
          <button
            onClick={() => setIsAdjustOpen(true)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              color: 'var(--theme-text-primary, #f8fafc)',
              fontWeight: 600,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            ⚖️ {t('inventory.actions.adjustStock') || 'Adjust / Write-off'}
          </button>
          <button
            onClick={() => setIsImportOpen(true)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              color: 'var(--theme-text-primary, #f8fafc)',
              fontWeight: 600,
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            📂 {t('inventory.actions.importStaged') || 'Import'}
          </button>
          <button
            onClick={handleExportCsv}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              color: 'var(--theme-text-secondary, #94a3b8)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            CSV Export
          </button>
          <button
            onClick={handleExportJson}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              border: '1px solid var(--theme-border, #1e293b)',
              color: 'var(--theme-text-secondary, #94a3b8)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            JSON Export
          </button>
        </div>
      </div>

      {/* Metrics Summary Strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>
            {t('inventory.metrics.onHand') || 'Total On-Hand'}
          </div>
          <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', unicodeBidi: 'isolate' }}>{totalOnHand}</div>
        </div>
        <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>
            {t('inventory.metrics.reserved') || 'Active Reservations'}
          </div>
          <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', color: '#f59e0b', unicodeBidi: 'isolate' }}>{totalReserved}</div>
        </div>
        <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>
            Quality Quarantine
          </div>
          <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', color: '#f43f5e', unicodeBidi: 'isolate' }}>{totalQuarantined}</div>
        </div>
        <div style={{ padding: '14px', borderRadius: '10px', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)' }}>
          <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>
            {t('inventory.metrics.available') || 'Available To Sell'}
          </div>
          <div style={{ fontSize: '22px', fontWeight: 800, marginTop: '4px', color: 'var(--theme-status-success-text, #34d399)', unicodeBidi: 'isolate' }}>
            {totalAvailable}
          </div>
        </div>
      </div>

      {/* Location Filter Pills */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)' }}>Location:</span>
        <button
          onClick={() => setSelectedLocation('all')}
          style={{
            padding: '6px 14px',
            borderRadius: '20px',
            border: selectedLocation === 'all' ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
            backgroundColor: selectedLocation === 'all' ? 'var(--theme-accent-subtle, rgba(56, 189, 248, 0.12))' : 'transparent',
            color: selectedLocation === 'all' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-secondary, #94a3b8)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          🌐 {t('inventory.locations.all') || 'All Locations'}
        </button>
        {locations.map((loc) => (
          <button
            key={loc.locationId}
            onClick={() => setSelectedLocation(loc.locationId)}
            style={{
              padding: '6px 14px',
              borderRadius: '20px',
              border: selectedLocation === loc.locationId ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
              backgroundColor: selectedLocation === loc.locationId ? 'var(--theme-accent-subtle, rgba(56, 189, 248, 0.12))' : 'transparent',
              color: selectedLocation === loc.locationId ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-secondary, #94a3b8)',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {loc.isRetailCounter ? '📟 ' : '🏭 '} {loc.name}
          </button>
        ))}
      </div>

      {/* Stock Table */}
      <div
        style={{
          backgroundColor: 'var(--theme-bg-card, #151d2a)',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: '12px',
          overflowX: 'auto',
          overflowY: 'visible',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px', minWidth: '920px' }}>
          <thead
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: 'var(--theme-bg-card, #151d2a)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <tr style={{ borderBottom: '1px solid var(--theme-border, #1e293b)', color: 'var(--theme-text-muted, #94a3b8)', fontSize: '11px', textTransform: 'uppercase' }}>
              <th style={{ padding: '12px 16px' }}>SKU</th>
              <th style={{ padding: '12px 16px' }}>Product</th>
              <th style={{ padding: '12px 16px' }}>Location</th>
              <th style={{ padding: '12px 16px', textAlign: 'right' }}>On-Hand</th>
              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Reserved</th>
              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Quarantined</th>
              <th style={{ padding: '12px 16px', textAlign: 'right' }}>Available</th>
              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Batches</th>
              <th style={{ padding: '12px 16px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projections.map((p) => {
              const isExpanded = expandedSku === p.sku;
              const isLow = p.availableToSell <= p.lowStockThreshold;
              return (
                <React.Fragment key={`${p.sku}_${p.locationId}`}>
                  <tr
                    onClick={() => setExpandedSku(isExpanded ? null : p.sku)}
                    style={{
                      borderBottom: '1px solid var(--theme-border, #1e293b)',
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? 'rgba(255, 255, 255, 0.03)' : 'transparent',
                    }}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: 700 }}>
                      <code style={{ unicodeBidi: 'isolate' }}>{p.sku}</code>
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {(() => {
                          const item = SECURITY_TACTICAL_CATALOG.find((cat) => cat.sku === p.sku);
                          return item?.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt={p.productName}
                              style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain', background: '#0b1120', border: '1px solid #334155' }}
                            />
                          ) : null;
                        })()}
                        <div>
                          <div>{p.productName}</div>
                          {(() => {
                            const allVars = globalCatalogService.getAllVariants();
                            const matchedVar = allVars.find((v) => v.sku === p.sku);
                            if (matchedVar?.basePriceMinor) {
                              return (
                                <span style={{ fontSize: '11px', color: '#34d399', backgroundColor: 'rgba(52, 211, 153, 0.12)', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 }}>
                                  ${(parseInt(matchedVar.basePriceMinor, 10) / 100).toFixed(2)}
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--theme-text-secondary, #94a3b8)' }}>{p.locationId}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, unicodeBidi: 'isolate' }}>{p.onHand}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#f59e0b', unicodeBidi: 'isolate' }}>{p.reserved}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#f43f5e', fontWeight: 600, unicodeBidi: 'isolate' }}>{p.quarantined || 0}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: isLow ? '#fb7185' : 'var(--theme-status-success-text, #34d399)', unicodeBidi: 'isolate' }}>
                      {p.availableToSell} {isLow && '⚠️'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: 'rgba(255, 255, 255, 0.08)' }}>
                        {p.batches.length} {isExpanded ? '▲' : '▼'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const allProds = globalCatalogService.getAllProducts();
                          const allVars = globalCatalogService.getAllVariants();
                          const matchedVar = allVars.find((v) => v.sku === p.sku);
                          const matchedProd = matchedVar ? allProds.find((pr) => pr.productId === matchedVar.productId) : allProds[0];
                          if (matchedProd) {
                            setEditingProductId(matchedProd.productId);
                          }
                        }}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(56, 189, 248, 0.15)',
                          color: '#38bdf8',
                          border: '1px solid rgba(56, 189, 248, 0.3)',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ✏️ Edit Listing
                      </button>
                    </td>
                  </tr>

                  {/* FEFO Batch Inspector */}
                  {isExpanded && (
                    <tr style={{ backgroundColor: 'rgba(0, 0, 0, 0.2)', borderBottom: '1px solid var(--theme-border, #1e293b)' }}>
                      <td colSpan={8} style={{ padding: '16px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase' }}>
                            FEFO Lot Batches & Quality Holds for {p.sku}
                          </div>
                          {p.allergenProfile && (
                            <div style={{ fontSize: '11px', color: '#38bdf8' }}>
                              🛡️ Certified Allergen Profile Verified
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                          {p.batches.map((b) => {
                            const health = healthService.evaluateBatchHealth(b);
                            const isExpired = health.freshness === 'expired';
                            const isNearExpiry = health.freshness === 'near_expiry';
                            const isQuar = b.quarantined > 0;

                            return (
                              <div
                                key={b.lotNumber}
                                style={{
                                  padding: '12px',
                                  borderRadius: '8px',
                                  border: isQuar ? '1px solid #f43f5e' : isExpired ? '1px solid #e11d48' : isNearExpiry ? '1px solid #f59e0b' : '1px solid var(--theme-border, #1e293b)',
                                  backgroundColor: 'var(--theme-bg-card, #151d2a)',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '8px',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontWeight: 700, fontSize: '13px' }}>Lot #{b.lotNumber}</span>
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      fontWeight: 700,
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      backgroundColor: isQuar ? '#881337' : isExpired ? '#4c0519' : isNearExpiry ? '#451a03' : '#064e3b',
                                      color: isQuar ? '#fda4af' : isExpired ? '#fb7185' : isNearExpiry ? '#fcd34d' : '#6ee7b7',
                                    }}
                                  >
                                    {isQuar ? `HOLD (${b.quarantined} QTY)` : isExpired ? 'EXPIRED' : isNearExpiry ? `NEAR EXPIRY (${health.daysUntilExpiry}d)` : 'FRESH'}
                                  </span>
                                </div>

                                <div style={{ fontSize: '11px', color: 'var(--theme-text-secondary, #94a3b8)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                                  <div>Expiry: <strong style={{ color: '#fff' }}>{b.expiryDate || 'N/A'}</strong></div>
                                  <div>Available: <strong style={{ color: '#34d399' }}>{b.availableToSell}</strong></div>
                                  <div>On Hand: {b.onHand}</div>
                                  <div>Reserved: {b.reserved}</div>
                                </div>

                                {/* Batch Operational Action Buttons */}
                                <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
                                  {b.availableToSell > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuarantineTarget({ batch: b, mode: 'hold' });
                                        setQuarantineQuantity(b.availableToSell);
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid #f43f5e',
                                        backgroundColor: 'transparent',
                                        color: '#f43f5e',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      🛡️ Quality Hold
                                    </button>
                                  )}
                                  {b.quarantined > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setQuarantineTarget({ batch: b, mode: 'release' });
                                        setQuarantineQuantity(b.quarantined);
                                      }}
                                      style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        border: '1px solid #34d399',
                                        backgroundColor: 'transparent',
                                        color: '#34d399',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                      }}
                                    >
                                      ✅ Release Hold
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenRecallTrace(b.lotNumber);
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      border: '1px solid var(--theme-border, #1e293b)',
                                      backgroundColor: 'transparent',
                                      color: 'var(--theme-text-secondary, #94a3b8)',
                                      fontSize: '10px',
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    🔍 Trace Recall
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quarantine Hold / Release Modal */}
      {quarantineTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ width: '420px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: '0 0 8px' }}>
              {quarantineTarget.mode === 'hold' ? '🛡️ Place Batch on Quality Hold' : '✅ Release Batch from Hold'}
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)', margin: '0 0 16px' }}>
              SKU: {quarantineTarget.batch.sku} | Lot: {quarantineTarget.batch.lotNumber}
            </p>
            <form onSubmit={handleQuarantineSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Quantity</label>
                <input
                  type="number"
                  min={1}
                  max={quarantineTarget.mode === 'hold' ? quarantineTarget.batch.availableToSell : quarantineTarget.batch.quarantined}
                  value={quarantineQuantity}
                  onChange={(e) => setQuarantineQuantity(Number(e.target.value))}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              {quarantineTarget.mode === 'hold' && (
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Reason Code</label>
                  <select
                    value={quarantineReasonCode}
                    onChange={(e) => setQuarantineReasonCode(e.target.value as QuarantineReasonCode)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                  >
                    <option value="damaged_packaging">Damaged Packaging</option>
                    <option value="temperature_excursion">Temperature Excursion</option>
                    <option value="recall_investigation">Recall Investigation</option>
                    <option value="lab_testing">Lab Testing / COA Verification</option>
                    <option value="near_expiry_segregation">Near Expiry Segregation</option>
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Audit Notes</label>
                <textarea
                  value={quarantineNotes}
                  onChange={(e) => setQuarantineNotes(e.target.value)}
                  placeholder="Mandatory quality hold explanation..."
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff', height: '60px' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button type="button" onClick={() => setQuarantineTarget(null)} style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: quarantineTarget.mode === 'hold' ? '#f43f5e' : '#34d399', color: '#000', fontWeight: 700, cursor: 'pointer' }}>
                  {quarantineTarget.mode === 'hold' ? 'Confirm Hold' : 'Confirm Release'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lot Recall Reverse-Traceability Dialog */}
      {recallTargetLot && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ width: '560px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: '0 0 8px', color: '#fb7185' }}>
              🚨 Recall Reverse-Traceability: Lot #{recallTargetLot}
            </h2>
            <p style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)', margin: '0 0 16px' }}>
              Historical customer orders fulfilled with items from this lot:
            </p>

            {affectedOrders.length === 0 ? (
              <div style={{ padding: '16px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,0.02)', textAlign: 'center', color: 'var(--theme-text-muted, #94a3b8)', fontSize: '13px' }}>
                No historical sales found for this batch lot. Shelf stock only.
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                {affectedOrders.map((ord) => (
                  <div key={ord.orderId} style={{ padding: '8px 12px', borderRadius: '6px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--theme-border, #1e293b)', fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span><strong>Order #{ord.orderId}</strong> ({ord.quantity} units)</span>
                    <span style={{ color: 'var(--theme-text-muted, #94a3b8)' }}>{ord.purchasedAt.slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={() => setRecallTargetLot(null)} style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer' }}>Close</button>
              {affectedOrders.length > 0 && (
                <button type="button" onClick={handleGenerateIncident} style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#e11d48', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  Create Incident Ticket
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Receive Stock Modal */}
      {isReceiveOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ width: '420px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: '0 0 16px' }}>📥 Receive Inbound Stock</h2>
            <form onSubmit={handleReceiveSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>SKU</label>
                <input
                  type="text"
                  value={receiveForm.sku}
                  onChange={(e) => setReceiveForm({ ...receiveForm, sku: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Location</label>
                <select
                  value={receiveForm.locationId}
                  onChange={(e) => setReceiveForm({ ...receiveForm, locationId: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                >
                  {locations.map((l) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Lot Number</label>
                <input
                  type="text"
                  value={receiveForm.lotNumber}
                  onChange={(e) => setReceiveForm({ ...receiveForm, lotNumber: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Expiry Date (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={receiveForm.expiryDate}
                  onChange={(e) => setReceiveForm({ ...receiveForm, expiryDate: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={receiveForm.quantity}
                  onChange={(e) => setReceiveForm({ ...receiveForm, quantity: Number(e.target.value) })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button type="button" onClick={() => setIsReceiveOpen(false)} style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#38bdf8', color: '#000', fontWeight: 700, cursor: 'pointer' }}>Receive</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {isTransferOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ width: '420px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: '0 0 16px' }}>🔄 Dual-Legged Transfer</h2>
            <form onSubmit={handleTransferSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>SKU</label>
                <input
                  type="text"
                  value={transferForm.sku}
                  onChange={(e) => setTransferForm({ ...transferForm, sku: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Source</label>
                  <select
                    value={transferForm.sourceLocationId}
                    onChange={(e) => setTransferForm({ ...transferForm, sourceLocationId: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                  >
                    {locations.map((l) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Target</label>
                  <select
                    value={transferForm.targetLocationId}
                    onChange={(e) => setTransferForm({ ...transferForm, targetLocationId: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                  >
                    {locations.map((l) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Lot Number</label>
                <input
                  type="text"
                  value={transferForm.lotNumber}
                  onChange={(e) => setTransferForm({ ...transferForm, lotNumber: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={transferForm.quantity}
                  onChange={(e) => setTransferForm({ ...transferForm, quantity: Number(e.target.value) })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button type="button" onClick={() => setIsTransferOpen(false)} style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#38bdf8', color: '#000', fontWeight: 700, cursor: 'pointer' }}>Execute Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Adjust / Write-off Modal */}
      {isAdjustOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ width: '420px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: '0 0 16px' }}>⚖️ Adjust / Write-Off Stock</h2>
            <form onSubmit={handleAdjustSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>SKU</label>
                <input
                  type="text"
                  value={adjustForm.sku}
                  onChange={(e) => setAdjustForm({ ...adjustForm, sku: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Location</label>
                  <select
                    value={adjustForm.locationId}
                    onChange={(e) => setAdjustForm({ ...adjustForm, locationId: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                  >
                    {locations.map((l) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Action Type</label>
                  <select
                    value={adjustForm.type}
                    onChange={(e) => setAdjustForm({ ...adjustForm, type: e.target.value as any })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                  >
                    <option value="adjustment">Stock Count Adjustment</option>
                    <option value="writeoff">Damaged / Spoilage Write-Off</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Lot Number</label>
                <input
                  type="text"
                  value={adjustForm.lotNumber}
                  onChange={(e) => setAdjustForm({ ...adjustForm, lotNumber: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Quantity Delta (Negative to deduct)</label>
                <input
                  type="number"
                  value={adjustForm.quantityDelta}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantityDelta: Number(e.target.value) })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', textTransform: 'uppercase', fontWeight: 700 }}>Reason (Mandatory Audit Note)</label>
                <input
                  type="text"
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'var(--theme-bg-input, #0b0f19)', color: '#fff' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                <button type="button" onClick={() => setIsAdjustOpen(false)} style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: adjustForm.type === 'writeoff' ? '#fb7185' : '#38bdf8', color: '#000', fontWeight: 700, cursor: 'pointer' }}>
                  {adjustForm.type === 'writeoff' ? 'Confirm Write-Off' : 'Apply Adjustment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Staged Import Modal */}
      {isImportOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ width: '600px', backgroundColor: 'var(--theme-bg-card, #151d2a)', border: '1px solid var(--theme-border, #1e293b)', borderRadius: '12px', padding: '20px', maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 800, margin: '0 0 8px' }}>📂 Staged Bulk Stock Import</h2>
            <p style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)', margin: '0 0 16px' }}>
              Paste CSV or JSON payload. Dry-run schema validation executes first before any live movements are committed.
            </p>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input type="radio" checked={importFormat === 'csv'} onChange={() => setImportFormat('csv')} /> CSV
              </label>
              <label style={{ fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input type="radio" checked={importFormat === 'json'} onChange={() => setImportFormat('json')} /> JSON
              </label>
            </div>

            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              style={{
                width: '100%',
                height: '140px',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid var(--theme-border, #1e293b)',
                backgroundColor: 'var(--theme-bg-input, #0b0f19)',
                color: '#fff',
                fontFamily: 'monospace',
                fontSize: '12px',
                marginBottom: '12px',
              }}
            />

            <button
              onClick={handleValidateImport}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: 'var(--theme-accent, #38bdf8)',
                color: '#000',
                fontWeight: 700,
                fontSize: '12px',
                cursor: 'pointer',
                marginBottom: '16px',
              }}
            >
              🔍 Dry-Run Validate
            </button>

            {importReport && (
              <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(0,0,0,0.3)', border: '1px solid var(--theme-border, #1e293b)', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '6px' }}>
                  Validation Results: {importReport.accepted} accepted, {importReport.rejected} rejected
                </div>
                {importReport.errors.length > 0 && (
                  <div style={{ color: '#fb7185', fontSize: '12px' }}>
                    {importReport.errors.map((e, idx) => (
                      <div key={idx}>Row {e.row}: {e.message}</div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => { setIsImportOpen(false); setImportReport(null); }} style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--theme-border, #1e293b)', backgroundColor: 'transparent', color: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button
                disabled={!importReport || importReport.accepted === 0}
                onClick={handleCommitImport}
                style={{
                  padding: '8px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: importReport && importReport.accepted > 0 ? '#34d399' : 'var(--theme-border, #1e293b)',
                  color: '#000',
                  fontWeight: 700,
                  cursor: importReport && importReport.accepted > 0 ? 'pointer' : 'not-allowed',
                }}
              >
                Commit Valid Movements
              </button>
              </div>
            </div>
          </div>
        )}

      {/* Create Product & Taxonomy Modal */}
      <CreateProductModal
        isOpen={isCreateProductOpen}
        authContext={authContext}
        onClose={() => setIsCreateProductOpen(false)}
        onCreated={refreshData}
      />

      {/* Governed Listing Editor Modal */}
      <EditListingModal
        isOpen={!!editingProductId}
        productId={editingProductId}
        authContext={authContext}
        onClose={() => setEditingProductId(null)}
        onSaved={refreshData}
      />
    </div>
  );
};
